import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { StarRating } from "@/components/StarRating";
import { Loader2, Check, X, Star, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const AdminReviews = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reviews", { params: { status: filter === "all" ? undefined : filter } });
      setItems(res.data);
    } catch (err) {
      if (err.response?.status === 401) { setAdminToken(null); navigate("/admin/login"); }
      else toast.error("Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/reviews/${id}`, body);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this review? This can't be undone.")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <AdminLayout
      eyebrow="Customer feedback"
      title="Reviews"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {[
            { v: "pending", label: "Pending" },
            { v: "approved", label: "Approved" },
            { v: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setFilter(t.v)}
              className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition ${filter === t.v ? "bg-spark-yellow text-spark-ink border-spark-yellow" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
              data-testid={`reviews-filter-${t.v}`}
            >{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400"><Loader2 className="size-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] py-16 text-center">
            <MessageSquare className="size-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-300 font-display uppercase tracking-wider">Nothing here</p>
            <p className="text-sm text-slate-500 mt-1">Reviews will appear here as customers submit them.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => (
              <li key={r.id} className={`rounded-xl border ${r.approved ? "border-white/[0.07] bg-white/[0.04]" : "border-spark-yellow/30 bg-spark-yellow/[0.04]"} p-5`} data-testid={`admin-review-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <StarRating value={r.rating} size={16} />
                      {r.featured && <span className="bg-spark-yellow text-spark-ink text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm">Featured</span>}
                      {r.approved ? (
                        <span className="text-emerald-400 text-[10px] uppercase tracking-wider font-bold">Approved</span>
                      ) : (
                        <span className="text-spark-yellow text-[10px] uppercase tracking-wider font-bold">Pending</span>
                      )}
                    </div>
                    {r.title && <p className="font-display uppercase tracking-tight text-base font-bold text-white">{r.title}</p>}
                    <p className="text-sm text-slate-200 mt-2 whitespace-pre-line">{r.body}</p>
                    <p className="text-xs text-slate-500 mt-3">
                      <span className="font-bold text-slate-300">{r.customer_name}</span>
                      {r.location && ` · ${r.location}`}
                      {r.job_ref && ` · ${r.job_ref}`}
                      {` · ${new Date(r.created_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    {!r.approved && (
                      <button onClick={() => patch(r.id, { approved: true })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-[10px] uppercase tracking-wider font-bold" data-testid={`approve-${r.id}`}>
                        <Check className="size-3.5" /> Approve
                      </button>
                    )}
                    {r.approved && (
                      <button onClick={() => patch(r.id, { approved: false })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 text-[10px] uppercase tracking-wider font-bold">
                        <X className="size-3.5" /> Unpublish
                      </button>
                    )}
                    <button onClick={() => patch(r.id, { featured: !r.featured })} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold ${r.featured ? "bg-spark-yellow text-spark-ink" : "bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"}`} data-testid={`feature-${r.id}`}>
                      <Star className={`size-3.5 ${r.featured ? "fill-spark-ink" : ""}`} /> {r.featured ? "Featured" : "Feature"}
                    </button>
                    <button onClick={() => remove(r.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-[10px] uppercase tracking-wider font-bold" data-testid={`delete-review-${r.id}`}>
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
